/**
 * CoSingery — **意見回饋彈窗**（Beta 測試期；`decisions/043`）
 *
 * 產品負責人 2026-09-25：「might also **gather opinion for UX upgrad**?」
 *
 * 設計：
 *   · 用共用 `@coeldery/bottom-sheet`（同歌詞彈窗／改名彈窗同一個外殼）
 *   · 四種 kind（體驗／問題／想法／其他）＋ **1–5 分**（可選）＋ 一句話／一段字
 *   · ⚠️ **明文講清楚**：你（產品負責人）會睇到；**唔會**自動附帶歌詞／日記
 *   · 提交結果**照實講**（成功／失敗原因）——唔准扮成功
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import BottomSheet from '@coeldery/bottom-sheet';

const KINDS = ['ux', 'bug', 'idea', 'other'] as const;
type Kind = (typeof KINDS)[number];

const CHIP = (on: boolean): React.CSSProperties => ({
  padding: '5px 11px',
  borderRadius: '999px',
  border: `1px solid ${on ? 'var(--color-accent)' : 'var(--color-divider)'}`,
  background: on ? 'var(--color-accent)' : 'transparent',
  color: on ? 'var(--color-on-accent)' : 'var(--color-text)',
  fontSize: 'var(--font-size-small, 15px)',
  cursor: 'pointer',
});

const BTN: React.CSSProperties = {
  padding: '10px 16px',
  borderRadius: '999px',
  fontSize: 'var(--font-size-button, 18px)',
  cursor: 'pointer',
};

export default function FeedbackSheet({
  open,
  onClose,
  locale,
  page,
  appVersion,
}: {
  open: boolean;
  onClose: () => void;
  locale: string;
  /** 邊一頁撳入嚟（`me`／`chat`…）—— 只作分類用 */
  page: string;
  /** 外殼版本（診斷用；由呼叫者傳入） */
  appVersion?: string;
}) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<Kind>('ux');
  const [score, setScore] = useState<number | null>(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const submit = async () => {
    setBusy(true);
    setResult(null);
    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind,
          ...(score === null ? {} : { score }),
          message,
          page,
          locale,
          ...(appVersion ? { appVersion } : {}),
        }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (res.ok) {
        setResult({ ok: true, text: t('feedback_thanks') });
        setMessage('');
        setScore(null);
        return;
      }
      setResult({
        ok: false,
        text:
          data.error === 'message_required'
            ? t('feedback_error_empty')
            : data.error === 'too_many_feedback'
              ? t('feedback_error_too_many')
              : `${t('feedback_error_generic')}（${data.error ?? res.status}）`,
      });
    } catch (err) {
      setResult({ ok: false, text: `${t('feedback_error_generic')}（${String(err).slice(0, 60)}）` });
    } finally {
      setBusy(false);
    }
  };

  return (
    <BottomSheet
      open={open}
      titleKey="feedback_title"
      onClose={onClose}
      actions={
        <>
          <button
            type="button"
            data-testid="feedback-send"
            disabled={busy || message.trim().length === 0}
            onClick={() => void submit()}
            style={{
              ...BTN,
              border: '1px solid var(--color-accent)',
              background: 'var(--color-accent)',
              color: 'var(--color-on-accent)',
              opacity: busy || message.trim().length === 0 ? 0.6 : 1,
            }}
          >
            {busy ? t('feedback_sending') : t('feedback_send')}
          </button>
          <button
            type="button"
            data-testid="feedback-cancel"
            disabled={busy}
            onClick={onClose}
            style={{ ...BTN, border: '1px solid var(--color-divider)', background: 'transparent', color: 'var(--color-text)' }}
          >
            {t('feedback_cancel')}
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: '10px' }} data-testid="feedback-sheet">
        {/* ⚠️ 明文講清楚：你會睇到；**唔會**自動帶歌詞／日記 */}
        <p className="footnote" style={{ margin: 0 }} data-testid="feedback-privacy">
          {t('feedback_privacy_note')}
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {KINDS.map((k) => (
            <button
              key={k}
              type="button"
              data-testid={`feedback-kind-${k}`}
              aria-pressed={kind === k}
              onClick={() => setKind(k)}
              style={CHIP(kind === k)}
            >
              {t(`feedback_kind_${k}`)}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          <span className="footnote">{t('feedback_score_label')}</span>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              data-testid={`feedback-score-${n}`}
              aria-pressed={score === n}
              onClick={() => setScore(score === n ? null : n)}
              style={CHIP(score === n)}
            >
              {n}
            </button>
          ))}
        </div>

        <textarea
          data-testid="feedback-message"
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, 2000))}
          placeholder={t('feedback_placeholder')}
          rows={5}
          style={{
            width: '100%',
            fontSize: 'var(--font-size-body, 19px)',
            padding: '10px 12px',
            borderRadius: '10px',
            border: '1px solid var(--color-divider)',
            background: 'var(--color-bg)',
            color: 'var(--color-text)',
            resize: 'vertical',
          }}
        />

        {result ? (
          <p
            role={result.ok ? 'status' : 'alert'}
            data-testid="feedback-result"
            style={{ margin: 0, color: result.ok ? 'var(--color-text)' : 'var(--color-danger, #b3261e)' }}
          >
            {result.text}
          </p>
        ) : null}
      </div>
    </BottomSheet>
  );
}

